"use client";

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import Link from "next/link";
import { Menu, X, ChevronDown, Sun, Moon, LayoutDashboard, ArrowRight } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { BrandLogo } from "./BrandLogo";
import { visiblePillars } from "@/lib/services/pillars";

const NAV = [
  { name: "Portfolio", href: "/portfolio" },
  { name: "Über uns", href: "/ueber-uns" },
  { name: "Blog", href: "/blog" },
  { name: "Kontakt", href: "/kontakt" },
];

/**
 * Kopfzeile mit Mega-Menü „Dienstleistungen“ (Auftrag Paket 12, Nr. 1):
 * drei Säulen als Spalten aus src/lib/services/pillars.ts – dieselbe Quelle
 * wie „Unsere Dienstleistungen“ auf der Startseite. Das Panel steht immer
 * im Server-HTML (Crawler, KI-Agenten) und wird nur per `hidden` geschaltet:
 * kein Layout-Sprung, keine Opacity-Einblendung. Bedienung nach dem
 * WAI-ARIA-Disclosure-Vorgehen: Klick/Enter öffnet (bewusst kein Öffnen
 * beim Überfahren – das kollidierte mit dem Klick), Escape schließt und
 * setzt den Fokus zurück, Tab aus dem Panel heraus schließt, Pfeiltasten
 * wandern durch die Einträge. Mobil: aufklappbare Gruppen je Säule.
 */
export function Header({
  visibleServiceSlugs,
}: {
  /** Öffentlich sichtbare Dienstleistungen (Admin → Dienstleistungen). */
  visibleServiceSlugs?: string[];
} = {}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  // Pfeil nach unten auf dem Button: Panel öffnen und den ersten Eintrag fokussieren, sobald es gerendert ist.
  const [focusFirst, setFocusFirst] = useState(false);
  const pillars = visiblePillars(visibleServiceSlugs);
  const panelId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Nur toggleTheme aus dem Context – das Lesen von `theme` würde einen
  // Hydration-Mismatch (#418) auslösen, weil der Server immer „light"
  // rendert. Stattdessen schalten wir Visuals über CSS-Variable und
  // Klassen-Selektoren auf `[data-theme="dark"]` (Header-Hintergrund,
  // Theme-Toggle-Icon).
  const { toggleTheme } = useTheme();
  // null = noch nicht geprüft, true = eingeloggt, false = ausgeloggt
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    // supabase-js erst im Leerlauf nach der Hydration nachladen – sonst
    // steckt das Paket (~65 KB) im kritischen Pfad jeder öffentlichen Seite.
    // Der Login-Status steuert nur den „Dashboard“-Link oben rechts.
    const idle = (cb: () => void) =>
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(cb, { timeout: 2500 })
        : window.setTimeout(cb, 1500);
    idle(() => {
      if (!active) return;
      import("@/lib/supabase/browser")
        .then(({ getSupabaseBrowser }) => {
          if (!active) return;
          const supabase = getSupabaseBrowser();
          supabase.auth.getUser().then(({ data }) => {
            if (active) setSignedIn(!!data.user);
          });
          const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            setSignedIn(!!session?.user);
          });
          unsubscribe = () => sub.subscription.unsubscribe();
        })
        .catch(() => {
          if (active) setSignedIn(false);
        });
    });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  // Klick außerhalb schließt das Mega-Menü.
  useEffect(() => {
    if (!servicesOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [servicesOpen]);

  useEffect(() => {
    if (!servicesOpen || !focusFirst) return;
    panelRef.current?.querySelector<HTMLAnchorElement>("a[href]")?.focus();
    setFocusFirst(false);
  }, [servicesOpen, focusFirst]);

  const closeAndFocus = () => {
    setServicesOpen(false);
    buttonRef.current?.focus();
  };

  const panelLinks = () => Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>("a[href]") ?? []);

  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeAndFocus();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Home" || e.key === "End") {
      const links = panelLinks();
      if (links.length === 0) return;
      e.preventDefault();
      const idx = links.indexOf(document.activeElement as HTMLAnchorElement);
      let next = 0;
      if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % links.length;
      if (e.key === "ArrowUp") next = idx <= 0 ? links.length - 1 : idx - 1;
      if (e.key === "End") next = links.length - 1;
      links[next]?.focus();
    }
  };

  const onButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setServicesOpen(true);
      setFocusFirst(true);
    }
    if (e.key === "Escape" && servicesOpen) {
      e.preventDefault();
      setServicesOpen(false);
    }
  };

  // Tab aus dem Menü heraus (Button oder Panel) schließt es.
  const onMenuBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (menuRef.current && !menuRef.current.contains(e.relatedTarget as Node | null)) setServicesOpen(false);
  };

  const navLink = "rounded-md px-3 py-2 text-sm font-medium transition-colors duration-100 hover:bg-gray-100";

  return (
    <header
      className="sticky top-0 z-50 border-b backdrop-blur-xl"
      style={{ backgroundColor: "var(--bg-header-glass)", borderColor: "var(--border)" }}
    >
      <nav className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8" aria-label="Hauptnavigation">
        {/* Logo */}
        <Link href="/" className="flex items-center">
          <BrandLogo priority />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1 lg:flex">
          <Link href="/" className={navLink} style={{ color: "var(--text-secondary)" }}>
            Startseite
          </Link>

          {/* Mega-Menü Dienstleistungen */}
          <div ref={menuRef} onBlur={onMenuBlur}>
            <button
              ref={buttonRef}
              type="button"
              className={`flex items-center gap-1 ${navLink}`}
              style={{ color: "var(--text-secondary)" }}
              aria-expanded={servicesOpen}
              aria-controls={panelId}
              onClick={() => setServicesOpen((o) => !o)}
              onKeyDown={onButtonKeyDown}
            >
              Dienstleistungen
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {/* Panel immer im DOM (Server-HTML), nur per hidden geschaltet. */}
            <div
              id={panelId}
              ref={panelRef}
              hidden={!servicesOpen}
              onKeyDown={onPanelKeyDown}
              className="absolute left-0 right-0 top-full pt-2"
            >
              <div
                className="mx-4 rounded-2xl border p-6 lg:mx-8"
                style={{ backgroundColor: "var(--card-bg)", borderColor: "var(--border)", boxShadow: "var(--card-shadow-lg)" }}
              >
                <div className={`grid gap-8 ${pillars.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
                  {pillars.map((pillar) => (
                    <div key={pillar.id}>
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
                          <pillar.icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>{pillar.label}</p>
                          <p className="mt-0.5 text-sm" style={{ color: "var(--text-tertiary)" }}>{pillar.tagline}</p>
                        </div>
                      </div>
                      <ul className="mt-4 space-y-1" aria-label={`Leistungen ${pillar.label}`}>
                        {pillar.services.map((s) => (
                          <li key={s.href}>
                            <Link
                              href={s.href}
                              onClick={() => setServicesOpen(false)}
                              className="flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-brand-500/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                            >
                              <s.icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden="true" />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold" style={{ color: "var(--text)" }}>{s.title}</span>
                                <span className="block text-sm" style={{ color: "var(--text-tertiary)" }}>{s.short}</span>
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--border)" }}>
                  <Link
                    href="/dienstleistungen"
                    onClick={() => setServicesOpen(false)}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-brand-500 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                  >
                    Alle Dienstleistungen
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {NAV.map((n) => (
            <Link key={n.href} href={n.href} className={navLink} style={{ color: "var(--text-secondary)" }}>
              {n.name}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="rounded-lg p-2 transition-colors duration-100 hover:bg-gray-100" aria-label="Theme wechseln">
            {/* Beide Icons werden gerendert, CSS blendet das aktive aus (kein Hydration-Mismatch). */}
            <Moon className="dmd-theme-icon-light h-5 w-5" style={{ color: "var(--text-tertiary)" }} />
            <Sun className="dmd-theme-icon-dark h-5 w-5" style={{ color: "var(--text-tertiary)" }} />
          </button>

          {signedIn ? (
            <Link href="/dashboard" className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-100 hover:bg-gray-100 lg:flex" style={{ color: "var(--text-secondary)" }}>
              <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
              Dashboard
            </Link>
          ) : (
            <Link href="/login" className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-100 hover:bg-gray-100 lg:flex" style={{ color: "var(--text-secondary)" }}>
              Login
            </Link>
          )}

          <Link href="/beratung" className="hidden rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 lg:block">
            Kostenlose Beratung
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X className="h-5 w-5" style={{ color: "var(--text)" }} /> : <Menu className="h-5 w-5" style={{ color: "var(--text)" }} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div id="mobile-menu" className="border-t px-4 py-4 lg:hidden" style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)" }}>
          <div className="flex flex-col gap-1">
            <Link href="/" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium" style={{ color: "var(--text)" }}>
              Startseite
            </Link>
            <Link href="/dienstleistungen" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold" style={{ color: "var(--text)" }}>
              Alle Dienstleistungen
            </Link>
            {pillars.map((pillar) => {
              const open = !!openGroups[pillar.id];
              const listId = `${panelId}-m-${pillar.id}`;
              return (
                <div key={pillar.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium"
                    style={{ color: "var(--text)" }}
                    aria-expanded={open}
                    aria-controls={listId}
                    onClick={() => setOpenGroups((g) => ({ ...g, [pillar.id]: !open }))}
                  >
                    <span className="flex items-center gap-2">
                      <pillar.icon className="h-4 w-4 text-brand-500" aria-hidden="true" />
                      {pillar.label}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  <ul id={listId} hidden={!open} className="mb-1">
                    {pillar.services.map((s) => (
                      <li key={s.href}>
                        <Link href={s.href} onClick={() => setMobileOpen(false)} className="block rounded-lg px-9 py-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {NAV.map((n) => (
              <Link key={n.href} href={n.href} onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium" style={{ color: "var(--text)" }}>
                {n.name}
              </Link>
            ))}
            <div className="my-2 border-t" style={{ borderColor: "var(--border)" }} />
            {signedIn ? (
              <Link href="/dashboard" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium" style={{ color: "var(--text)" }}>
                Dashboard
              </Link>
            ) : (
              <Link href="/login" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium" style={{ color: "var(--text)" }}>
                Login
              </Link>
            )}
            <Link href="/beratung" onClick={() => setMobileOpen(false)} className="mt-1 rounded-lg bg-brand-500 px-4 py-2.5 text-center text-sm font-semibold text-white">
              Kostenlose Beratung
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
